import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { brand, services, remodeling, painting, roofline } from "../data/site";


const LangToggle = ({ className = "" }) => {
  const { pathname } = useLocation();
  const isEs = pathname.startsWith("/espanol");
  const base = "px-2.5 py-1 text-xs font-semibold uppercase tracking-wider rounded transition-colors";
  return (
    <div className={`inline-flex items-center rounded-md border border-white/20 p-0.5 ${className}`} role="group" aria-label="Language">
      <Link to="/" lang="en" aria-current={!isEs ? "page" : undefined}
        className={`${base} ${!isEs ? "bg-brand text-ink" : "text-white/70 hover:text-brand"}`}>EN</Link>
      <Link to="/espanol" lang="es" aria-current={isEs ? "page" : undefined}
        className={`${base} ${isEs ? "bg-brand text-ink" : "text-white/70 hover:text-brand"}`}>ES</Link>
    </div>
  );
};

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Service Areas", path: "/service-areas" },
  { name: "Resources", path: "/resources" },
  // Nav label is short on purpose — the full page title, "Understanding
  // Your Warranty", wrapped the bar onto two lines at 1024px.
  { name: "Your Warranty", path: "/understanding-your-warranty" },
];

// Contact is a menu rather than a single link. About and Join Our Team moved
// off the top level into it, which keeps the bar to five items on the widths
// where the logo is widest.
const contactLinks = [
  { name: "Contact Us", path: "/contact" },
  { name: "About", path: "/about" },
  { name: "Financing", path: "/financing" },
  { name: "Make a Payment", path: "/make-a-payment" },
  { name: "Join Our Team", path: "/join-our-team" },
  { name: "Warranty Claim Center", path: "/warranty-claim" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setContactOpen(false);
  }, [location.pathname]);

  // Escape closes the mobile menu and the services dropdown. Without it a
  // keyboard user who opens either one has no way out but to tab through it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      setServicesOpen(false);
      setContactOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      lang="en"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/95 backdrop-blur-md shadow-lg py-1.5" : "bg-gradient-to-b from-black/75 to-transparent py-2.5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        {/* Full badge, wordmark included. It needs real height to stay legible,
            so the header is taller than a text logo would need. */}
        <Link to="/" className="shrink-0" aria-label={`${brand.name} — home`}>
          <img
            fetchPriority="high"
            decoding="async"
            src={brand.logo}
            alt={brand.name}
            width="820"
            height="814"
            className="h-14 sm:h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          <Link to="/" className="text-white/90 hover:text-brand font-medium text-sm tracking-wide link-underline">Home</Link>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setServicesOpen(false); }}
          >
            <button type="button" aria-expanded={servicesOpen} onFocus={() => setServicesOpen(true)} className="flex items-center gap-1 text-white/90 hover:text-brand font-medium text-sm tracking-wide">
              Services <ChevronDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="bg-ink border border-white/10 rounded-xl shadow-2xl w-64 overflow-hidden">
                  <Link to="/services" className="block px-5 py-3 text-brand hover:bg-white/5 font-semibold text-sm border-b border-white/10">
                    All Services
                  </Link>
                  {services.filter((x) => !x.hiddenFromNav).map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      className="block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors"
                    >
                      {s.name}
                    </Link>
                  ))}
                  {/* Not a /services/* page — storm and insurance work has its
                      own page, so it is listed by hand rather than coming out
                      of the services array. */}
                  <Link
                    to="/insurance-claims"
                    className="block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors border-t border-white/10"
                  >
                    Storm &amp; Insurance
                  </Link>
                  {remodeling.enabled && (
                    <Link
                      to="/remodeling"
                      className="block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors border-t border-white/10"
                    >
                      Home Remodeling
                    </Link>
                  )}
                  {roofline.enabled && (
                    <Link
                      to="/attic-roofline"
                      className="block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors border-t border-white/10"
                    >
                      Attic &amp; Roofline
                    </Link>
                  )}
                  {painting.enabled && (
                    <Link
                      to="/painting"
                      className="block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors"
                    >
                      Interior &amp; Exterior Painting
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
          {navLinks.slice(1).map((l) => (
            <Link key={l.path} to={l.path} className="text-white/90 hover:text-brand font-medium text-sm tracking-wide link-underline">
              {l.name}
            </Link>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setContactOpen(true)}
            onMouseLeave={() => setContactOpen(false)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setContactOpen(false); }}
          >
            <button type="button" aria-expanded={contactOpen} onFocus={() => setContactOpen(true)} className="flex items-center gap-1 text-white/90 hover:text-brand font-medium text-sm tracking-wide">
              Contact <ChevronDown size={16} className={`transition-transform ${contactOpen ? "rotate-180" : ""}`} />
            </button>
            {contactOpen && (
              <div className="absolute top-full right-0 pt-4">
                <div className="bg-ink border border-white/10 rounded-xl shadow-2xl w-60 overflow-hidden">
                  {contactLinks.map((l, i) => (
                    <Link
                      key={l.path}
                      to={l.path}
                      className={i === 0
                        ? "block px-5 py-3 text-brand hover:bg-white/5 font-semibold text-sm border-b border-white/10"
                        : "block px-5 py-3 text-white/80 hover:text-brand hover:bg-white/5 text-sm transition-colors"}
                    >
                      {l.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          <a href={`tel:${brand.phoneRaw}`} className="hidden xl:flex items-center gap-2 text-white/90 hover:text-brand text-sm font-medium">
            <Phone size={16} className="text-brand" /> {brand.phone}
          </a>
          <Link
            to="/contact"
            className="bg-brand hover:bg-brand-light text-ink font-semibold text-sm px-5 py-2.5 rounded-md transition-colors tracking-wide"
          >
            Free Estimate
          </Link>
        </div>

        {/* Mobile toggle */}
        <button type="button" className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0d0d0d] border-t border-white/10 mt-2 shadow-2xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            <LangToggle className="self-start mb-3" />
            <Link to="/" className="text-white/90 py-3 border-b border-white/5">Home</Link>
            <div className="py-3 border-b border-white/5">
              <button type="button" aria-expanded={servicesOpen} onClick={() => setServicesOpen(!servicesOpen)} className="flex items-center justify-between w-full text-white/90">
                Services <ChevronDown size={18} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 mt-2 flex flex-col gap-2">
                  <Link to="/services" className="text-brand text-sm py-1">All Services</Link>
                  {services.filter((x) => !x.hiddenFromNav).map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="text-white/70 text-sm py-1">{s.name}</Link>
                  ))}
                  <Link to="/insurance-claims" className="text-white/70 text-sm py-1">Storm &amp; Insurance</Link>
                  {remodeling.enabled && (
                    <Link to="/remodeling" className="text-white/70 text-sm py-1">Home Remodeling</Link>
                  )}
                  {roofline.enabled && (
                    <Link to="/attic-roofline" className="text-white/70 text-sm py-1">Attic &amp; Roofline</Link>
                  )}
                  {painting.enabled && (
                    <Link to="/painting" className="text-white/70 text-sm py-1">Interior &amp; Exterior Painting</Link>
                  )}
                </div>
              )}
            </div>
            {navLinks.slice(1).map((l) => (
              <Link key={l.path} to={l.path} className="text-white/90 py-3 border-b border-white/5">{l.name}</Link>
            ))}
            <div className="py-3 border-b border-white/5">
              <button type="button" aria-expanded={contactOpen} onClick={() => setContactOpen(!contactOpen)} className="flex items-center justify-between w-full text-white/90">
                Contact <ChevronDown size={18} className={`transition-transform ${contactOpen ? "rotate-180" : ""}`} />
              </button>
              {contactOpen && (
                <div className="pl-4 mt-2 flex flex-col gap-2">
                  {contactLinks.map((l, i) => (
                    <Link key={l.path} to={l.path} className={i === 0 ? "text-brand text-sm py-1" : "text-white/70 text-sm py-1"}>
                      {l.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-2 text-brand py-3 font-medium">
              <Phone size={16} /> {brand.phone}
            </a>
            <Link to="/contact" className="bg-brand text-ink font-semibold text-center px-5 py-3 rounded-md mt-2">
              Get a Free Estimate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
